import React, { useMemo, useLayoutEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { gsap } from 'gsap';

/** ---------------------------
 * Internal helpers
 * --------------------------- */
function startPlus30(target) {
  const n = Number.isFinite(target) ? target : 0;
  return Math.max(0, Math.round(n * 1.3));
}

function intDigits(value) {
  const n = Math.max(0, Number.isFinite(value) ? Math.floor(value) : 0);
  return String(n);
}

function buildAlignedDigitStrings(targetValue, startValue) {
  const t = intDigits(targetValue);
  const s = intDigits(startValue);
  const len = Math.max(t.length, s.length);
  return {
    paddedTarget: t.padStart(len, '0'),
    paddedStart: s.padStart(len, '0'),
    len,
  };
}

function buildScaffold(len) {
  const scaffold = [];
  for (let i = 0; i < len; i++) {
    const remaining = len - (i + 1);
    scaffold.push({ type: 'digit', index: i });
    if (remaining > 0 && remaining % 3 === 0) {
      scaffold.push({ type: 'sep', char: ',' });
    }
  }
  return scaffold;
}

/** ---------------------------
 * DigitReel (internal)
 * --------------------------- */
const DigitReel = ({
  startDigit,
  targetDigit,
  fontSize,
  color,
  lineHeight = 1.0,
  weight = 700,
  duration = 2.4,
  playSignal = 0,
  extraLoops = 3,
}) => {
  const containerRef = useRef(null);
  const reelRef = useRef(null);
  const tweenRef = useRef(null);

  const digits = useMemo(() => {
    const arr = [startDigit];
    let current = startDigit;
    // full spins first
    const fullSteps = Math.max(0, Math.floor(extraLoops)) * 10;
    for (let i = 0; i < fullSteps; i++) {
      current = (current - 1 + 10) % 10;
      arr.push(current);
    }
    // then step down to target
    const finalSteps = (current - targetDigit + 10) % 10;
    for (let i = 0; i < finalSteps; i++) {
      current = (current - 1 + 10) % 10;
      arr.push(current);
    }
    return arr;
  }, [startDigit, targetDigit, extraLoops]);

  useLayoutEffect(() => {
    if (!containerRef.current || !reelRef.current) return;

    const ctx = gsap.context(() => {
      const items = reelRef.current.children;
      if (!items.length) return;
      const itemHeight = items[0].getBoundingClientRect().height || 0;
      const endIndex = digits.length - 1;

      if (tweenRef.current) tweenRef.current.kill();

      tweenRef.current = gsap.fromTo(
        reelRef.current,
        { y: 0 },
        {
          y: -endIndex * itemHeight,
          duration,
          ease: 'power2.out',
          paused: true,
        }
      );
    }, containerRef);

    return () => {
      if (tweenRef.current) tweenRef.current.kill();
      ctx.revert();
    };
  }, [digits, duration]);

  useLayoutEffect(() => {
    if (tweenRef.current) {
      tweenRef.current.play(0);
    }
  }, [playSignal]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        height: `calc(${fontSize} * ${lineHeight})`,
        width: 'ch',
        display: 'inline-flex',
        verticalAlign: 'bottom',
      }}
    >
      <Box ref={reelRef} sx={{ display: 'flex', flexDirection: 'column' }}>
        {digits.map((d, i) => (
          <Typography
            key={`${d}-${i}`}
            sx={{
              fontFamily: 'Inter',
              fontWeight: weight,
              fontSize,
              lineHeight,
              letterSpacing: '-0.02em',
              color,
              fontVariantNumeric: 'tabular-nums',
              textAlign: 'right',
              height: `calc(${fontSize} * ${lineHeight})`,
            }}
          >
            {d}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

/** ---------------------------
 * Public component: RollingNumber
 * --------------------------- */
const RollingNumber = ({
  value,
  prefix = null, // { text: 'R', sx: {...} }
  suffix = null, // { text: 'pm', sx: {...} }
  digitSx = {}, // styles applied to every digit reel + separators
  wrapperSx = {}, // wrapper styles
  duration = 2.4,
  extraLoops = 3,
  playSignal = 0,
  animate = true,
}) => {
  const numericValue = useMemo(() => {
    const n = typeof value === 'string' ? parseInt(value, 10) : Number(value);
    return Number.isFinite(n) ? n : 0;
  }, [value]);

  const startValue = useMemo(() => startPlus30(numericValue), [numericValue]);

  const { paddedTarget, paddedStart, len } = useMemo(
    () => buildAlignedDigitStrings(numericValue, startValue),
    [numericValue, startValue]
  );

  const scaffold = useMemo(() => buildScaffold(len), [len]);

  const digitTypographySx = {
    fontFamily: 'Inter',
    fontWeight: digitSx.fontWeight ?? 700,
    lineHeight: digitSx.lineHeight ?? 1,
    letterSpacing: digitSx.letterSpacing ?? '-0.02em',
    ...digitSx,
  };

  const shouldAnimate = animate && playSignal > 0;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'baseline',
        columnGap: '4px',
        ...wrapperSx,
      }}
    >
      {/* PREFIX */}
      {prefix && (
        <Typography
          sx={{
            ...digitTypographySx,
            ...(prefix.sx || {}),
          }}
        >
          {prefix.text}
        </Typography>
      )}

      {/* DIGITS */}
      <Box sx={{ display: 'inline-flex', alignItems: 'baseline' }}>
        {(() => {
          let di = 0;
          let hasShownAnyDigit = false;

          return scaffold.map((slot, i) => {
            if (slot.type === 'sep') {
              if (!hasShownAnyDigit) return null;
              return (
                <Typography
                  key={`sep-${i}`}
                  sx={{
                    ...digitTypographySx,
                    px: '2px',
                  }}
                >
                  {slot.char}
                </Typography>
              );
            }

            const myIndex = di++;
            const startDigit = parseInt(paddedStart[myIndex], 10);
            const targetDigit = parseInt(paddedTarget[myIndex], 10);

            const isLeadingZero =
              !hasShownAnyDigit && targetDigit === 0 && myIndex < len - 1;

            if (isLeadingZero) return null;

            hasShownAnyDigit = true;

            if (!shouldAnimate) {
              return (
                <Typography key={`d-${i}`} sx={digitTypographySx}>
                  {targetDigit}
                </Typography>
              );
            }

            return (
              <DigitReel
                key={`d-${i}`}
                startDigit={startDigit}
                targetDigit={targetDigit}
                fontSize={digitTypographySx.fontSize || '3rem'}
                color={digitTypographySx.color || '#1E354E'}
                lineHeight={digitTypographySx.lineHeight || 1}
                weight={digitTypographySx.fontWeight || 700}
                duration={duration}
                playSignal={playSignal}
                extraLoops={extraLoops}
              />
            );
          });
        })()}
      </Box>

      {/* SUFFIX */}
      {suffix && (
        <Typography
          sx={{
            fontFamily: 'Inter',
            fontWeight: 700,
            lineHeight: 1,
            color: digitTypographySx.color,
            ...suffix.sx,
          }}
        >
          {suffix.text}
        </Typography>
      )}
    </Box>
  );
};

export default RollingNumber;
